import type { CtaTrigger, CtaType, SiteCtaSettings, SitePopupSettings } from "@/types/cta.types"

/**
 * Adds only the trigger switches that a legacy CTA implicitly enabled.
 *
 * Older CTAs have no `enable_*` fields. A missing trigger value meant the
 * popup was immediate, so adding `false` flags while editing would change its
 * saved shape and break the connector's legacy immediate behaviour.
 */
export function inferLegacyTriggerEnableFlags(trigger: CtaTrigger): Partial<CtaTrigger> {
  return {
    ...(trigger.enable_delay === undefined && (trigger.show_after_seconds ?? 0) > 0
      ? { enable_delay: true }
      : {}),
    ...(trigger.enable_scroll === undefined && (trigger.show_on_scroll_percent ?? 0) > 0
      ? { enable_scroll: true }
      : {}),
    ...(trigger.enable_exit_intent === undefined && trigger.show_on_exit_intent
      ? { enable_exit_intent: true }
      : {}),
  }
}

/**
 * Builds the editor's form state for a CTA loaded from the API.
 *
 * Defaults fill in the top level and targeting, but deliberately NOT the
 * trigger: `DEFAULT_SITE_CTA_SETTINGS.trigger` carries `enable_*: false` plus a
 * 10s delay, and force-merging those onto a saved CTA would rewrite it on the
 * next save — either to "never show" (all switches false) or to a 10s delay it
 * never had. Only the switches a legacy trigger implied are added.
 */
export function mergeLoadedSiteSettings(
  defaults: SiteCtaSettings,
  existing: Partial<SiteCtaSettings> | undefined,
): SiteCtaSettings {
  const existingTrigger = existing?.trigger ?? {}
  return {
    ...defaults,
    ...existing,
    targeting: { ...defaults.targeting, ...existing?.targeting },
    trigger: { ...existingTrigger, ...inferLegacyTriggerEnableFlags(existingTrigger) },
  }
}

// A floating bar has no priority selector and no session-limit policy: it is
// simply expected to appear, as it did before popup orchestration existed. The
// connector now treats a present `enable_*` switch as authoritative, so the
// shared default trigger (every switch off) would leave a new bar permanently
// hidden. These overrides restore the pre-feature behaviour of showing after a
// short delay, and keep the switch UI honest about it.
const FLOATING_BAR_TRIGGER_OVERRIDES: Partial<CtaTrigger> = {
  show_after_seconds: 5,
  enable_delay: true,
}

/** The trigger a brand-new CTA of this type starts from. */
export function defaultTriggerForType(type: CtaType, defaultTrigger: CtaTrigger): CtaTrigger {
  return type === "floating-bar"
    ? { ...defaultTrigger, ...FLOATING_BAR_TRIGGER_OVERRIDES }
    : defaultTrigger
}

// Every CtaTrigger value is a primitive, so key-wise equality is enough to tell
// an untouched default apart from something the user configured.
function isSameTrigger(a: CtaTrigger, b: CtaTrigger): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]) as Set<keyof CtaTrigger>
  return [...keys].every((key) => a[key] === b[key])
}

/**
 * Resolves the trigger after the user switches a CTA's type in the editor.
 *
 * Type is chosen after the form is initialised (every new CTA starts as a
 * banner), so the type-specific defaults have to follow that switch or a new
 * floating bar would inherit the popup defaults and never display. A trigger
 * the user already edited is returned untouched — only defaults are re-based.
 */
export function triggerForTypeChange(
  current: CtaTrigger,
  previousType: CtaType,
  nextType: CtaType,
  defaultTrigger: CtaTrigger,
): CtaTrigger {
  return isSameTrigger(current, defaultTriggerForType(previousType, defaultTrigger))
    ? defaultTriggerForType(nextType, defaultTrigger)
    : current
}

/** The popup-settings endpoint validates this one root-level field. */
export function popupSettingsUpdatePayload(settings: SitePopupSettings): SitePopupSettings {
  return { max_popups_per_session: settings.max_popups_per_session }
}
